import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { baseUrl } from '../../shared.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
  PieChart, Pie, ResponsiveContainer,
} from 'recharts';

const PASS_COLOR = '#4472C4';
const FAIL_COLOR = '#ED7D31';

const PIE_COLORS = [
  '#4472C4', '#ED7D31', '#A9D18E', '#FF0000', '#FFC000',
  '#5B9BD5', '#70AD47', '#264478', '#9E480E', '#636363',
  '#997300', '#255E91', '#43682B', '#698ED0', '#F4B183',
];

const STATUSES = ['PASS', 'FAIL', 'NTI'];

const buildCustomerTypeData = (rows) => {
  const types = [...new Set(rows.map(r => r.type))];
  const customerMap = {};
  rows.forEach(r => {
    if (!customerMap[r.customer]) customerMap[r.customer] = { customer: r.customer };
    customerMap[r.customer][`${r.type}_pass`] = r.pass;
    customerMap[r.customer][`${r.type}_fail`] = r.fail;
  });
  return { types, data: Object.values(customerMap) };
};

function PlatformDashboard() {
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'year'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSiteLocations, setSelectedSiteLocations] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ customers: [], siteLocations: [], types: [] });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);

  const initialOptions = useRef({ customers: [], siteLocations: [], types: [] });
  const initializedRef = useRef(false);
  const skipNextSiteEffect = useRef(false);

  const callDependentOptions = (customers, siteLocations) => {
    fetch(baseUrl + 'dashboard/dependentOptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken'),
      },
      body: JSON.stringify({ customers, siteLocations }),
    })
      .then(r => r.json())
      .then(data => setFilterOptions(prev => ({ ...prev, siteLocations: data.siteLocations, types: data.types })))
      .catch(err => console.error('dependentOptions error', err));
  };

  // Mount: load all options and initial summary
  useEffect(() => {
    fetch(baseUrl + 'dashboard/filterOptions', {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken'),
      },
    })
      .then(r => r.json())
      .then(data => {
        initialOptions.current = data;
        setFilterOptions(data);
        initializedRef.current = true;
      })
      .catch(err => console.error('filterOptions error', err));

    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Customer change: narrow site and type options; clear downstream selections
  useEffect(() => {
    if (!initializedRef.current) return;
    skipNextSiteEffect.current = selectedSiteLocations.length > 0;
    setSelectedSiteLocations([]);
    setSelectedTypes([]);
    if (selectedCustomers.length === 0) {
      setFilterOptions(prev => ({
        ...prev,
        siteLocations: initialOptions.current.siteLocations,
        types: initialOptions.current.types,
      }));
    } else {
      callDependentOptions(selectedCustomers, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  // Site change: narrow type options; clear type selection
  useEffect(() => {
    if (!initializedRef.current) return;
    if (skipNextSiteEffect.current) {
      skipNextSiteEffect.current = false;
      return;
    }
    setSelectedTypes([]);
    if (selectedSiteLocations.length === 0) {
      if (selectedCustomers.length === 0) {
        setFilterOptions(prev => ({ ...prev, types: initialOptions.current.types }));
      } else {
        callDependentOptions(selectedCustomers, []);
      }
    } else {
      callDependentOptions(selectedCustomers, selectedSiteLocations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteLocations]);

  const fetchSummary = async (overrides = {}) => {
    setLoading(true);
    const payload = {
      dateFrom: (overrides.dateFrom ?? dateFrom)?.format('YYYY-MM-DD') ?? null,
      dateTo: (overrides.dateTo ?? dateTo)?.format('YYYY-MM-DD') ?? null,
      customers: overrides.customers ?? selectedCustomers,
      siteLocations: overrides.siteLocations ?? selectedSiteLocations,
      types: overrides.types ?? selectedTypes,
      statuses: overrides.statuses ?? selectedStatuses,
    };
    try {
      const res = await fetch(baseUrl + 'dashboard/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'username': localStorage.getItem('username'),
          'bToken': localStorage.getItem('bToken'),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setDashboardData(data);
      setLoadedAt(new Date().toLocaleString());
    } catch (err) {
      console.error('summary error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => fetchSummary();

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await fetch(baseUrl + 'dashboard/exportReport', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'username': localStorage.getItem('username'),
          'bToken': localStorage.getItem('bToken'),
        },
        body: JSON.stringify({
          dateFrom: dateFrom?.format('YYYY-MM-DD') ?? null,
          dateTo: dateTo?.format('YYYY-MM-DD') ?? null,
          customers: selectedCustomers,
          siteLocations: selectedSiteLocations,
          types: selectedTypes,
          statuses: selectedStatuses,
          format,
        }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? 'InspectionReport.pdf' : 'InspectionReport.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleClear = () => {
    const from = dayjs().subtract(1, 'year');
    const to = dayjs();
    setDateFrom(from);
    setDateTo(to);
    skipNextSiteEffect.current = true;
    setSelectedCustomers([]);
    setSelectedSiteLocations([]);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setFilterOptions(initialOptions.current);
    fetchSummary({
      dateFrom: from, dateTo: to,
      customers: [], siteLocations: [], types: [], statuses: [],
    });
  };

  const multiSelectLabel = arr => arr.length === 0 ? 'All' : arr.join(', ');

  if (localStorage.getItem('canViewPlatformDashboard') !== '1') {
    return <Navigate to="/" />;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
        Platform Reporting Dashboard
      </Typography>

      {dashboardData && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <strong>Reportable Units in Period:</strong> {dashboardData.reportableUnits}
            {loadedAt && <span style={{ marginLeft: 16, color: 'var(--muted)', fontSize: 13 }}>as at {loadedAt}</span>}
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            <strong>Failure Rate — </strong>
            {dashboardData.failureRateByCategory.map(c => (
              <span key={c.category} style={{ marginRight: 24 }}>
                {c.category}: {c.failureRate.toFixed(2)}%
              </span>
            ))}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* Filter panel */}
        <Box sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 }, maxWidth: { md: 300 }, flexShrink: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 2, p: 2, boxShadow: 'var(--shadow-sm)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Filters</Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ mb: 2 }}>
              <MobileDatePicker
                label="Date From"
                value={dateFrom}
                onChange={v => setDateFrom(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <MobileDatePicker
                label="Date To"
                value={dateTo}
                onChange={v => setDateTo(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              multiple
              value={selectedCustomers}
              onChange={e => setSelectedCustomers(e.target.value)}
              label="Customer"
              renderValue={multiSelectLabel}
            >
              {filterOptions.customers.map(c => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={selectedCustomers.includes(c)} />
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Site Location</InputLabel>
            <Select
              multiple
              value={selectedSiteLocations}
              onChange={e => setSelectedSiteLocations(e.target.value)}
              label="Site Location"
              renderValue={multiSelectLabel}
            >
              {filterOptions.siteLocations.map(s => (
                <MenuItem key={s} value={s}>
                  <Checkbox checked={selectedSiteLocations.includes(s)} />
                  <ListItemText primary={s} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              value={selectedTypes}
              onChange={e => setSelectedTypes(e.target.value)}
              label="Type"
              renderValue={multiSelectLabel}
            >
              {filterOptions.types.map(t => (
                <MenuItem key={t} value={t}>
                  <Checkbox checked={selectedTypes.includes(t)} />
                  <ListItemText primary={t} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={selectedStatuses}
              onChange={e => setSelectedStatuses(e.target.value)}
              label="Status"
              renderValue={multiSelectLabel}
            >
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  <Checkbox checked={selectedStatuses.includes(s)} />
                  <ListItemText primary={s} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" size="small" onClick={handleApply} disabled={loading}>
              Apply
            </Button>
            <Button variant="outlined" size="small" onClick={handleClear} disabled={loading}>
              Clear
            </Button>
            <Button variant="contained" size="small" onClick={() => handleExport('excel')} disabled={exporting || !dashboardData}>
              Export Excel
            </Button>
            <Button variant="contained" size="small" color="error" onClick={() => handleExport('pdf')} disabled={exporting || !dashboardData}>
              Export PDF
            </Button>
          </Box>
        </Box>

        {/* Charts area — stacks below filters on mobile (ResponsiveContainer keeps charts fluid) */}
        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && dashboardData && (
            <>
              {/* Row 1: Pass/Fail + Type Pie */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Overall Pass / Fail</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={[
                      { name: 'PASS', value: dashboardData.passFailSummary.pass },
                      { name: 'FAIL', value: dashboardData.passFailSummary.fail },
                      { name: 'NTI', value: dashboardData.passFailSummary.nti },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" name="Count">
                        <Cell fill={PASS_COLOR} />
                        <Cell fill={FAIL_COLOR} />
                        <Cell fill="#A9A9A9" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Asset Type Breakdown</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={dashboardData.typeBreakdown}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                      >
                        {dashboardData.typeBreakdown.map((entry, index) => (
                          <Cell key={entry.type} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val, name) => [val, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Row 2: Units by Site */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Units by Site Location</Typography>
                <ResponsiveContainer width="100%" height={Math.max(200, dashboardData.unitsBySite.length * 36 + 40)}>
                  <BarChart layout="vertical" data={dashboardData.unitsBySite}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="siteLocation" type="category" width={160} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="pass" stackId="a" fill={PASS_COLOR} name="PASS" />
                    <Bar dataKey="fail" stackId="a" fill={FAIL_COLOR} name="FAIL" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Row 3: Per-customer Pass/Fail */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Pass / Fail by Customer</Typography>
                <ResponsiveContainer width="100%" height={Math.max(200, dashboardData.sitePassFail.length * 36 + 40)}>
                  <BarChart layout="vertical" data={dashboardData.sitePassFail}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="customer" type="category" width={140} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="pass" fill={PASS_COLOR} name="PASS" />
                    <Bar dataKey="fail" fill={FAIL_COLOR} name="FAIL" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Row 4: Customer × Type Breakdown */}
              {dashboardData.customerTypeBreakdown && dashboardData.customerTypeBreakdown.length > 0 && (() => {
                const { types, data } = buildCustomerTypeData(dashboardData.customerTypeBreakdown);
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Pass / Fail by Customer & Type</Typography>
                    <ResponsiveContainer width="100%" height={Math.max(260, data.length * (types.length * 22 + 20) + 60)}>
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="customer" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {types.flatMap((type, i) => {
                          const c = PIE_COLORS[i % PIE_COLORS.length];
                          return [
                            <Bar key={`${type}_pass`} dataKey={`${type}_pass`} name={`${type} - PASS`} fill={c} />,
                            <Bar key={`${type}_fail`} dataKey={`${type}_fail`} name={`${type} - FAIL`} fill={c} fillOpacity={0.4} stroke={c} strokeWidth={1} strokeDasharray="4 2" />,
                          ];
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                );
              })()}

            </>
          )}

          {!loading && !dashboardData && (
            <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
              Apply filters to load dashboard data.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default PlatformDashboard;
